import { TimePicker } from "@/components/TimePicker";
import { type CoWorkerScheduleActivity } from "@/features/calendar/types";
import { FormField, Input, Select } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  SectionContainer,
  TwoColumnElementsContainer,
  useSettings,
} from "@repo/ui";
import { isBefore } from "date-fns";
import { type Dispatch, type SetStateAction } from "react";

interface ScheduleActivityEditProps {
  item: CoWorkerScheduleActivity;
  setItem: Dispatch<SetStateAction<CoWorkerScheduleActivity>>;
}

export const ScheduleActivityEdit = ({
  item,
  setItem,
}: ScheduleActivityEditProps) => {
  const { task_configuration } = useSettings();

  const shiftTypes =
    task_configuration?.task_types
      .find((item) => item.name_key === "schedule")
      ?.subTypes?.map((subType) => ({
        value: subType.name_key,
        label: t(subType.translationKey ?? subType.name_key),
      })) ?? [];

  const handleFieldChange = (field: string, value: string) => {
    setItem((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <SectionContainer>
      <FormField shouldValidate={!item.activity} valid={Boolean(item.activity)}>
        <Input
          label={t("label.activity")}
          value={item.activity}
          onChange={(e) => handleFieldChange("activity", e.target.value)}
          type="text"
          id="activity-input"
        />
      </FormField>

      <FormField
        shouldValidate={!item.shiftType}
        valid={Boolean(item.shiftType)}
      >
        <Select
          label={t("label.shift_type")}
          value={item.shiftType?.toLowerCase()}
          id="shift-type-select"
          onChange={(e) => handleFieldChange("shiftType", e.target.value)}
        >
          {shiftTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </Select>
      </FormField>

      <TwoColumnElementsContainer>
        <TimePicker
          id="start-time"
          label={t("label.start_time")}
          value={new Date(item.start)}
          onChange={(newStartTime) => {
            let newEndTime = new Date(item.end);
            if (isBefore(newEndTime, newStartTime)) {
              newEndTime = newStartTime;
            }
            setItem((prev) => ({
              ...prev,
              start: newStartTime,
              end: newEndTime,
            }));
          }}
          timeIntervals={5}
          minTime={new Date()}
        />
        <TimePicker
          id="end-time"
          label={t("label.end_time")}
          value={new Date(item.end)}
          onChange={(newEndTime) => {
            setItem((prev) => ({
              ...prev,
              end: newEndTime,
            }));
          }}
          timeIntervals={5}
          minTime={new Date(item.start)}
        />
      </TwoColumnElementsContainer>
    </SectionContainer>
  );
};
